import { Directive, input, signal } from '@angular/core';

/**
 * Makes an element draggable with data attachment.
 * Uses native HTML5 drag API to transfer data via DataTransfer.
 */
@Directive({
    selector: '[qpxDraggable]',
    host: {
        '[attr.draggable]': '!disabled() ? "true" : "false"',
        '[class.dndDragging]': 'isDragging()',
        '[class.dndDraggableDisabled]': 'disabled()',
        '(dragstart)': 'onDragStart($event)',
        '(dragend)': 'onDragEnd()',
    },
})
export class QpxDraggable<T = unknown> {
    // --- Inputs ---

    /** The data to attach to the drag operation */
    public readonly qpxDraggable = input.required<T>();

    /** The allowed drop effect (move, copy, link, all, none) */
    public readonly dndEffectAllowed = input<
        'move' | 'copy' | 'link' | 'all' | 'none'
    >('move');

    /** Whether dragging is disabled */
    public readonly disabled = input(false);

    // --- State ---

    /** Whether the element is currently being dragged */
    protected readonly isDragging = signal(false);

    // --- Template methods ---

    /** Handles drag start event */
    protected onDragStart(e: DragEvent): void {
        if (this.disabled() || !e.dataTransfer) {
            e.preventDefault();
            return;
        }

        this.isDragging.set(true);

        // Set the allowed drop effect
        e.dataTransfer.effectAllowed = this.dndEffectAllowed();

        // Store the data as JSON in DataTransfer
        try {
            const data = this.qpxDraggable();
            e.dataTransfer.setData('application/json', JSON.stringify(data));
            // Also set as text/plain for compatibility
            e.dataTransfer.setData('text/plain', JSON.stringify(data));
        } catch (error) {
            console.error('Failed to serialize drag data:', error);
            e.preventDefault();
        }
    }

    /** Handles drag end event */
    protected onDragEnd(): void {
        this.isDragging.set(false);
    }
}
